/**
 * Single source of truth for the public free tools — powers the /tools hub, the
 * shared PublicHeader dropdown, the hub's JSON-LD, and the dynamic sitemap.
 *
 * Deliberately free of React/icon imports: `functions/sitemap.xml.ts` runs in a
 * Cloudflare Worker and imports this file, so pulling a component library in here
 * would drag it into the Worker bundle. Icons are referenced by key and resolved
 * to components at render time instead. Copy is referenced by translation key for
 * the same reason — i18next stays out of the Worker bundle, and the renderer
 * resolves the key with t().
 */
export interface FreeTool {
    /** Route path — also the canonical path used for SEO. */
    path: string
    /** Translation key for the tool name — matches the <h1> on the tool's own page. */
    nameKey: string
    /** Translation key for the hub card's one-liner. */
    descriptionKey: string
    /** Translation key for what the visitor walks away with, shown as the card's outcome line. */
    outcomeKey: string
    /** Which platform(s) the tool serves, shown as a chip. Proper nouns — not translated. */
    platform: string
    /** Resolved to an actual icon component by the page that renders the cards. */
    icon: ToolIconKey
    /** Tailwind classes for the icon tile — kept per-tool so cards stay distinguishable. */
    accent: string
}

export type ToolIconKey = 'calendar' | 'device' | 'chart' | 'photo' | 'money'

export const FREE_TOOLS: FreeTool[] = [
    {
        path: '/tools/content-calendar-generator',
        nameKey: 'tools.items.contentCalendarGenerator.name',
        descriptionKey: 'tools.items.contentCalendarGenerator.description',
        outcomeKey: 'tools.items.contentCalendarGenerator.outcome',
        platform: 'Instagram · TikTok · YouTube',
        icon: 'calendar',
        accent: 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400',
    },
    {
        path: '/tools/post-preview',
        nameKey: 'tools.items.postPreview.name',
        descriptionKey: 'tools.items.postPreview.description',
        outcomeKey: 'tools.items.postPreview.outcome',
        platform: 'Instagram · TikTok · YouTube Shorts',
        icon: 'device',
        accent: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
    },
    {
        path: '/tools/engagement-calculator',
        nameKey: 'tools.items.engagementCalculator.name',
        descriptionKey: 'tools.items.engagementCalculator.description',
        outcomeKey: 'tools.items.engagementCalculator.outcome',
        platform: 'Instagram · TikTok · YouTube',
        icon: 'chart',
        accent: 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400',
    },
    {
        path: '/tools/thumbnail-downloader',
        nameKey: 'tools.items.thumbnailDownloader.name',
        descriptionKey: 'tools.items.thumbnailDownloader.description',
        outcomeKey: 'tools.items.thumbnailDownloader.outcome',
        platform: 'YouTube · TikTok · Instagram',
        icon: 'photo',
        accent: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
    },
    {
        path: '/tools/tiktok-money-calculator',
        nameKey: 'tools.items.tiktokMoneyCalculator.name',
        descriptionKey: 'tools.items.tiktokMoneyCalculator.description',
        outcomeKey: 'tools.items.tiktokMoneyCalculator.outcome',
        platform: 'TikTok',
        icon: 'money',
        accent: 'bg-gray-900 text-white dark:bg-white dark:text-gray-900',
    },
]
